import type { Locale } from '../i18n';

interface Props {
  locale: Locale;
  size?: number;
  className?: string;
}

/**
 * Round country flag icon for the supported UI locales.
 * Each flag is rendered as a circle-clipped inline SVG so it stays crisp
 * at any size and never depends on system emoji rendering.
 */
export default function FlagIcon({ locale, size = 20, className = '' }: Props) {
  const props = { width: size, height: size, viewBox: '0 0 24 24', className };
  switch (locale) {
    case 'fr':
      return (
        <svg {...props} aria-label="Français">
          <defs>
            <clipPath id="flag-circle-fr">
              <circle cx="12" cy="12" r="12" />
            </clipPath>
          </defs>
          <g clipPath="url(#flag-circle-fr)">
            <rect x="0" y="0" width="8" height="24" fill="#0055A4" />
            <rect x="8" y="0" width="8" height="24" fill="#FFFFFF" />
            <rect x="16" y="0" width="8" height="24" fill="#EF4135" />
          </g>
        </svg>
      );
    case 'en':
      return (
        <svg {...props} aria-label="English">
          <defs>
            <clipPath id="flag-circle-gb">
              <circle cx="12" cy="12" r="12" />
            </clipPath>
          </defs>
          <g clipPath="url(#flag-circle-gb)">
            <rect width="24" height="24" fill="#012169" />
            {/* White diagonals (St Andrew's cross) */}
            <path d="M0,0 L24,24 M24,0 L0,24" stroke="#FFFFFF" strokeWidth="3" />
            {/* Red diagonals (St Patrick's cross), offset to one side */}
            <path d="M0,0 L24,24 M24,0 L0,24" stroke="#C8102E" strokeWidth="1.6" />
            {/* White cross (St George's, behind) */}
            <path d="M12,0 V24 M0,12 H24" stroke="#FFFFFF" strokeWidth="5" />
            {/* Red cross (St George's) */}
            <path d="M12,0 V24 M0,12 H24" stroke="#C8102E" strokeWidth="3" />
          </g>
        </svg>
      );
    case 'de':
      return (
        <svg {...props} aria-label="Deutsch">
          <defs>
            <clipPath id="flag-circle-de">
              <circle cx="12" cy="12" r="12" />
            </clipPath>
          </defs>
          <g clipPath="url(#flag-circle-de)">
            <rect x="0" y="0" width="24" height="8" fill="#000000" />
            <rect x="0" y="8" width="24" height="8" fill="#DD0000" />
            <rect x="0" y="16" width="24" height="8" fill="#FFCE00" />
          </g>
        </svg>
      );
    case 'it':
      return (
        <svg {...props} aria-label="Italiano">
          <defs>
            <clipPath id="flag-circle-it">
              <circle cx="12" cy="12" r="12" />
            </clipPath>
          </defs>
          <g clipPath="url(#flag-circle-it)">
            <rect x="0" y="0" width="8" height="24" fill="#009246" />
            <rect x="8" y="0" width="8" height="24" fill="#FFFFFF" />
            <rect x="16" y="0" width="8" height="24" fill="#CE2B37" />
          </g>
        </svg>
      );
    default:
      return null;
  }
}
